import type { Operator } from "@/providers/smarta-accounts/accounts-api-client";

/*
 * What type-to-find in an operator picker matches: the shown display name,
 * every name field of the person, the full name and the email. So "schmidt"
 * finds "Anni" (Anna Maria Schmidt), and so do "anna schmidt" and her email.
 */
const fullNameOf = ({ person }: Operator): string =>
  [person.givenNames, person.familyName].filter(Boolean).join(" ");

const operatorSearchTexts = (operator: Operator): Array<string | null> => [
  operator.person.displayName,
  operator.person.givenNames,
  operator.person.familyName,
  operator.person.preferredName,
  operator.person.email,
  fullNameOf(operator),
];

const containsInput = (input: string, texts: Array<string | null>) => {
  const needle = input.toLowerCase();
  return texts.some((text) => text?.toLowerCase().includes(needle));
};

/*
 * People type a full name with some names left out, such as "anna schmidt"
 * for "Anna Maria Schmidt". So every typed word must be in the full name.
 */
const containsEveryWord = (input: string, text: string) => {
  const words = input.toLowerCase().split(/\s+/).filter(Boolean);
  const haystack = text.toLowerCase();
  return words.length > 0 && words.every((word) => haystack.includes(word));
};

/** A case-insensitive search across the operator's names and email. */
export const matchesOperator = (input: string, operator: Operator) =>
  containsInput(input, operatorSearchTexts(operator)) ||
  containsEveryWord(input, fullNameOf(operator));

/**
 * The `filterOption` of every operator picker. The option's value is the
 * operator id, so the option finds its operator and matches all of its
 * names and email. An option that is not an operator (such as "Unassigned",
 * or a current assignee missing from the list) matches on its shown text,
 * the `label` or, for an `<Option>` child, the `children`.
 */
export const filterOperatorOption =
  (operators: Operator[]) =>
  (
    input: string,
    option?: { value?: unknown; label?: unknown; children?: unknown }
  ): boolean => {
    const operator = operators.find(({ id }) => id === option?.value);
    if (operator) return matchesOperator(input, operator);
    return containsInput(input, [
      String(option?.label ?? option?.children ?? ""),
    ]);
  };
